import { Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { ScopeOfWorkView } from "../../../shared/components/features/scope-of-work/view/ScopeOfWorkView";
import { ScopeOfWorkHandle } from "../../../shared/components/features/scope-of-work/controller/scope-of-work.handle";
import { ScopeOfWorkStep, type ScopeOfWorkState } from "../../../shared/components/features/scope-of-work/context/scope-of-work.types";
import type { Job } from "../../interfaces/job.interfaces";
import type { ScopeOfWork } from "../../../scope-of-work/types/scope-of-work.interface";
import { useModals } from "src/context/ModalContext";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { SASButtonStyles } from "../../../ui/styles/inputStyles";
import { mapScopeOfWorkToInitialPayload } from "../utils/mapScopeOfWorkToInitialPayload";
import { buildScopeOfWorkStatePatchPayload } from "../utils/buildScopeOfWorkStatePatchPayload";
import { usePatchJobScopeOfWork } from "../hooks/usePatchJobScopeOfWork";
import {
  scopeOfWorkCompleteValidator,
  scopeOfWorkStepValidator,
} from "../../../shared/components/features/scope-of-work/validators/scope-of-work-validators";

type Props = {
  job: Job;
  scopeOfWork: ScopeOfWork;
};

export function JobScopeOfWorkEdit({ job, scopeOfWork }: Props) {
  const { closeByKey } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const scopeRef = useRef<ScopeOfWorkHandle>(null);
  const [scopeState, setScopeState] = useState<ScopeOfWorkState | null>(null);
  const [isFirstScopeStep, setIsFirstScopeStep] = useState(true);
  const [isLastScopeStep, setIsLastScopeStep] = useState(false);
  const { mutate: patchScopeOfWork, isPending } = usePatchJobScopeOfWork();

  const [jobAlias, setJobAlias] = useState(job?.alias ?? "");
  const [jobDescription, setJobDescription] = useState(job?.description ?? "");

  const initialPayload = useMemo(() => mapScopeOfWorkToInitialPayload(scopeOfWork), [scopeOfWork]);

  const getCurrentState = useCallback(() => scopeRef.current?.getData() ?? scopeState, [scopeState]);

  const canProceedCurrentStep = useMemo(() => {
    const state = getCurrentState();
    if (!state) return false;
    if (state.step === ScopeOfWorkStep.Summary) return true;
    return scopeOfWorkStepValidator(state) === true;
  }, [getCurrentState, scopeState]);

  const isScopeComplete = useMemo(() => {
    const state = getCurrentState();
    if (!state) return false;
    return scopeOfWorkCompleteValidator(state) === true;
  }, [getCurrentState, scopeState]);

  const handleScopeStepChange = useCallback((_: ScopeOfWorkStep, isLastStep: boolean) => {
    setIsLastScopeStep(isLastStep);
  }, []);

  const handleScopeStateChange = useCallback((state: ScopeOfWorkState) => {
    setScopeState(state);
    setIsFirstScopeStep(state.step === ScopeOfWorkStep.Categories);
    setIsLastScopeStep(state.step === ScopeOfWorkStep.Summary);
  }, []);

  const handleSave = () => {
    const state = getCurrentState();
    if (!state) return;

    const validation = scopeOfWorkCompleteValidator(state);
    if (validation !== true) {
      showSnackbar(validation, "error");
      return;
    }

    const payload = buildScopeOfWorkStatePatchPayload(state, {
      clientUid: scopeOfWork.clientUid ?? job.clientUid ?? null,
      coverageAreaId: scopeOfWork.coverageAreaId ?? job.coverageArea?.id ?? null,
      companyId: scopeOfWork.companyId ?? null,
      frequencyType: state.frequencyType ?? scopeOfWork.frequencyType ?? "by_weekly",
    });

    patchScopeOfWork(
      { jobId: job.id, payload, alias: jobAlias, description: jobDescription },
      { onSuccess: () => closeByKey(`Job-Edit-ScopeOfWork-${job.id}`) },
    );
  };

  const handleBack = () => {
    scopeRef.current?.prev();
  };

  const handleNext = () => {
    const state = getCurrentState();
    if (!state) return;

    if (isLastScopeStep) {
      handleSave();
      return;
    }

    const validation = scopeOfWorkStepValidator(state);
    if (validation !== true) {
      showSnackbar(validation, "error");
      return;
    }

    scopeRef.current?.next();
  };

  return (
    <Box sx={{ height: "85vh", display: "flex", flexDirection: "column" }}>
      <Stack spacing={0.5} sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Edit scope of work</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Complete all 7 steps, then save your changes.</Typography>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <ScopeOfWorkView
          key={scopeOfWork.uid}
          ref={scopeRef}
          initialPayload={initialPayload as Partial<ScopeOfWorkState>}
          onStateChange={handleScopeStateChange}
          onStepChange={handleScopeStepChange}
          lockCategory
          showSummary={true}
          job={job}
          jobAlias={jobAlias}
          setJobAlias={setJobAlias}
          jobDescription={jobDescription}
          setJobDescription={setJobDescription}
        />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          px: 2,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => closeByKey(`Job-Edit-ScopeOfWork-${job.id}`)}
          disabled={isPending}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Cancel
        </Button>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button sx={SASButtonStyles} onClick={handleBack} disabled={isFirstScopeStep || isPending}>
            Back
          </Button>

          <Button
            sx={SASButtonStyles}
            variant="contained"
            onClick={handleNext}
            disabled={isPending || (isLastScopeStep ? !isScopeComplete : !canProceedCurrentStep)}
          >
            {isPending ? "Saving…" : isLastScopeStep ? "Save scope of work" : "Next"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
